import Bowser from 'bowser';
import { IPair, OperationStatusEnum } from '../interfaces';
import BigNumber from "bignumber.js";
import { FACTOR_PRICE_DECIMAL, FRONTEND_FEE_SCALE, MAX_TOKEN_DECIMAL } from "@libs/shared/constants/codex";
import { BLOCKCHAINS, WH_PROVIDER_CHAIN_NAME } from '../wallets/providers';

export const factorDecimal = FACTOR_PRICE_DECIMAL;

export const getDeviceInfo = () => {
  const browser = Bowser.getParser(window.navigator.userAgent);
  return `${browser.getBrowserName()} ${browser.getBrowserVersion()} ${browser.getEngineName()} ${browser.getOSName()}, ${browser.getPlatformType()}`;
}

export const addressFormate = (data: string, length = 4) => {
  if (!data?.length) return "";
  return `${data?.substring(0, length)}...${data?.substring(
    data.length - length,
    data.length
  )}`;
};

export const isKeyExpired = (date: string | number | Date) => {
  if (date === 0) return false; // infinite key
  if (!date) return true;
  return (Number(new Date(date)) - Date.now()) < 1;
};

export const getDecimalString = (amount: string, decimal: number) => {
  const value = amountValueFormate(amount, decimal);
  let a = String(value);
  let b = a.split('.').length === 2 ? a.split('.')[1].length : 0;
  return b;
};

export const getBaseDecimalLength = (pair: IPair) => {
  return getDecimalString(pair.min_size_increment, pair.base_decimal);
};

export const getPriceDecimalLength = (pair: IPair) => {
  return getDecimalString(pair.min_price_increment, factorDecimal);
};

export const listDateFormat = (date: Date | number, noTime= false) => {
  let newDAte = new Date(date);
  let parsedDate = newDAte.toLocaleDateString('default', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  let time = newDAte.toLocaleTimeString('en-US', {
    hour12: true,
    hour: '2-digit',
    minute: '2-digit',
  });

  return noTime ? parsedDate : parsedDate + ' ' + time;
};

export const getDate = (value: Date | number) => listDateFormat(value).split(" ")[0];
export const getTime = (value: Date | number) => `${listDateFormat(value).split(" ")[1]} ${listDateFormat(value).split(" ")[2]}`;

export const DateFormatToMonth = (date: Date | number) => {
  const newDAte = new Date(date);
  const day = newDAte.getUTCDate();
  const year= newDAte.getUTCFullYear();
  const month = newDAte.toLocaleString('default', { month: 'short' });
  const time = newDAte.toLocaleTimeString('en-US', {
    hour12: true,
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${month} ${day}, ${year} ${time}`
}

export const calculateExpireTime = (count: string | number) => {
  let days = Number(count);
  if (!days) return null;
  const expiredDate = Date.now() + days * 24 * 60 * 60 * 1000;
  return new Date(expiredDate)
}

export const getDaysDifference = (date: string | number | Date) => {
  if (!date) return "None";
  const targetDate = new Date(date);
  const nowDate = new Date();

  const differenceInTime =  targetDate.getTime() - nowDate.getTime();
  const differenceInDays = Math.floor(differenceInTime / (1000 * 60 * 60 * 24));
  if (differenceInDays < 0) return `Expired`;
  const word = differenceInDays === 0 ? "day" : "days";
  return `${differenceInDays + 1} ${word}`;
}

// 10.49986434 => 10.4
export const roundTo = (amount: string | number, decimal: number): string => {
  if (amount && decimal >= 0) {
    return BigNumber(amount).decimalPlaces(Number(decimal), 1).toString();
  }
  return "0";
};

// 1000000 => 1
export const amountFormate = (amount: string, decimal: number, toFixDecimal: number): string => {
  if (amount && BigNumber(amount).isGreaterThan(0) && decimal >= 0 && toFixDecimal >= 0) {
    return BigNumber(amount).shiftedBy(-Number(decimal)).decimalPlaces(Number(toFixDecimal), 1).toString();
  }
  return "0";
};

// 1000000 => 1
export const amountValueFormate = (amount: string | BigNumber, decimal: number): string => {
  if (amount && BigNumber(amount).isGreaterThan(0) && decimal >= 0) {
    return BigNumber(amount).shiftedBy(-Number(decimal)).decimalPlaces(MAX_TOKEN_DECIMAL, 1).toString();
  }
  return "0";
};

export const confertFactoredPrice = (value: string): string => {
  return amountValueFormate(value, FACTOR_PRICE_DECIMAL);
}

// 1 => 10000000
export const convertWithoutRounding = (value: string, decimal: number): string => {
  if (value && BigNumber(value).isGreaterThan(0) && decimal >= 0) {
    return BigNumber(value).shiftedBy(Number(decimal)).decimalPlaces(MAX_TOKEN_DECIMAL, 1).toString();
  }
  return "0";
};

// 123.456789 -> 123.4567 (allways 7 symb), 1000000 -> 1M
export const formatNumber = (balance: string) => {
  const integer = String(balance).split('.')[0].length;
  const decimal = (7 - integer) >= 0 ? (7 - integer) : 0;
  if (integer > 6 && integer < 10) {
    return `${amountFormate(balance, 6, 11 - integer)}M`;
  } else if (integer > 9 && integer < 13) {
    return `${amountFormate(balance, 9, 14 - integer)}B`;
  } else if (integer > 12) {
    return `${amountFormate(balance, 12, 17 - integer)}T`;
  }
  return new BigNumber(balance).decimalPlaces(decimal, 1).toString();
};

export const getPercentageOf = (total: string, value: string): number => {
  if (!value || !total) return 0;
  if (BigNumber(value).isGreaterThan(0) && BigNumber(total).isGreaterThan(0)) {
    return BigNumber(total).dividedBy(value).multipliedBy(100).decimalPlaces(0, 1).toNumber()
  }
  return 0
}

export const getTotalFromPercentage = (value: string, range: number): string => {
  return BigNumber(value).multipliedBy(range).dividedBy(100).toString();
}

export const getAvaragePrice = (depth: BigNumber, total: BigNumber, base_dec: number): BigNumber => {
  if (isNaN(base_dec) || !depth) return BigNumber(0);
  return total.shiftedBy(Number(base_dec)).dividedBy(depth);
}

export const flatValueFormate = (value: number): string => {
  if (value <= 0 || value === undefined) {
    return '0.00';
  }
  if (value < 1) {
    return currencyFormat(value, 7);
  }
  if (value < 10) {
    return currencyFormat(value, 4);
  }
  if (value < 9999) {
    return currencyFormat(value, 2);
  }
  return currencyFormat(value, 0);
};

const currencyFormat = (num, decimal = 2): string => {
  const money = new Intl.NumberFormat('us-US', {
    maximumFractionDigits: decimal,
  });
  return money.format(num);
};

export function numberWithSpaces(x: string) {
  var parts = x.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  if (parts[0] === '00') {
    parts[0] = parts[0].replace(/^0+/, '0');
  }
  if (parts[0] !== '0') {
    parts[0] = parts[0].replace(/^0+/, '');
  }
  return parts.join('.');
}

export function numberWithCommas(x: string | number): string {
  //@ts-ignore
  if (!x) return x;
  const parts = x.toString().split('.');

  // Insert commas in the integer portion
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Preserve a single leading zero for "0" or "0.xxx"
  if (parts[0] === '00') {
    parts[0] = parts[0].replace(/^0+/, '0');
  }
  // Strip all other leading zeros (e.g., "00123" -> "123")
  if (parts[0] !== '0') {
    parts[0] = parts[0].replace(/^0+/, '');
  }

  return parts.join('.');
}

// Function to apply the "10 symbol rule"
export function applyTenSymbolRule(value: number | string): string {
  const maxSymbols = 10;
  const maxDecimals = 9;
  const numValue = Number(value);
  const stringValue = value.toString();
  const [integerPart, decimalPart] = stringValue.split('.');
  if (numValue === 0) {
    return '0';
  }

  if (stringValue.length <= maxSymbols) {
    return stringValue;
  } else {
    if (integerPart.length >= maxSymbols) {
      return integerPart;
    }

    if (Math.abs(numValue) < Math.pow(10, -maxDecimals) && numValue >= 0) {
      const collapsedZeros = `0.${decimalPart.replace(/^0+/, '0...')}`;
      return collapsedZeros.substring(0, maxSymbols).replace(/0+$/, '');
    }

    if (stringValue.length >= maxSymbols && integerPart.length <= maxSymbols) {
      const spaceRemaining = maxSymbols - integerPart.length;
      const truncatedDecimalPart = decimalPart.substring(0, spaceRemaining);
      return `${integerPart}.${truncatedDecimalPart}`.replace(/\.0+$/, '').replace(/0+$/, '');
    }

    return integerPart;
  }
}

export function equalsIgnoreCase(str1: string | number, str2: string | number) {
  return String(str1).toUpperCase() === String(str2).toUpperCase();
}

export function findCorrectAsset(indx1: string, indx2: string, chain1: number, chain2: number) {
  return equalsIgnoreCase(indx1, indx2) && chain1 === chain2
}

export const convertSettingToBoolean = (setting: string, byDefault?: boolean | null) => {
  if (!setting) return byDefault;
  return setting !== "false"
};

export const convertJsonToArray = (setting: string | null) => JSON.parse(setting ?? "[]");
export const convertJsonToObject = (setting: string | null) => JSON.parse(setting ?? "{}");

export const sortByDate = (a: number | string | Date, b: number | string | Date) => new Date(b).getTime() - new Date(a).getTime();

export const sortUnitValues = (a: { value: string, decimal: number }, b: { value: string, decimal: number }, ascending = true)
  : number => {
  const AValue = Number(amountValueFormate(a.value, a.decimal));
  const BValue = Number(amountValueFormate(b.value, b.decimal));
  return ascending
    ? AValue - BValue
    : BValue - AValue
};

export const pause = (milliseconds: number) =>  new Promise(resolve => setTimeout(resolve, milliseconds));

export const convertFeeToPrecent = (value: string | number) => {
  if (Number(value) > 0) {
    return Number((Number(value) / FRONTEND_FEE_SCALE).toFixed(6));
  }
  return 0;
};

export const getFixedDecimal = (numStr: string, maxNumber = 9) => {
  if (!numStr) return 6;
  const digitsBeforeDecimal = numStr.split(".")[0];
  if (digitsBeforeDecimal === "0") return 6;
  return maxNumber - digitsBeforeDecimal.length > 6 ? 6 : maxNumber - digitsBeforeDecimal.length;
};

export const getColorStringByVarName = (name: string) => {
  const rootStyles = document.documentElement.style.cssText.split(";").map(el => el.split(":"));
  const color = rootStyles.find((el) => equalsIgnoreCase(el[0].trim(), name));
  return color[1].trim();
};

export const changeOpasity = (str: string, opasity: number) => {
  if (!str) return undefined; //importanf for chart if we dont have color in setting
  const elements = str.split(",");
  elements.splice(-1, 1, `${opasity})`);
  return elements.join(",");
}

export const getDepositTime = (chainId: number) => {
  // TO DO: improve
  return (WH_PROVIDER_CHAIN_NAME[chainId] === BLOCKCHAINS.ETHEREUM
    || WH_PROVIDER_CHAIN_NAME[chainId] === BLOCKCHAINS.SEPOLIA
    || WH_PROVIDER_CHAIN_NAME[chainId] === BLOCKCHAINS.ARBITRUM_ONE
    || WH_PROVIDER_CHAIN_NAME[chainId] === BLOCKCHAINS.ARBITRUM_SEPOLIA
    || WH_PROVIDER_CHAIN_NAME[chainId] === BLOCKCHAINS.BASE
    || WH_PROVIDER_CHAIN_NAME[chainId] === BLOCKCHAINS.BASE_SEPOLIA
    || WH_PROVIDER_CHAIN_NAME[chainId] === BLOCKCHAINS.OP_MAINNET
    || WH_PROVIDER_CHAIN_NAME[chainId] === BLOCKCHAINS.OP_SEPOLIA
  ) ? "5-15"  : "1";
}

export const isTxnDone = (status: OperationStatusEnum) => equalsIgnoreCase(status, OperationStatusEnum.Completed) || equalsIgnoreCase(status, OperationStatusEnum.Failed);
